/**
 * @fileoverview Azure Function Health Dashboard
 * @description React adaptation of the Azure Function endpoint compliance and health dashboard.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle2,
  CircleDashed,
  CircleOff,
  Play,
  ShieldAlert,
} from 'lucide-react';
import { HealthScenarioCard } from '../components';
import { useAzureFunctionHealth } from '../hooks/useAzureFunctionHealth';
import { DASHBOARD_FILTERS, HEALTH_ENVIRONMENTS } from '../constants';
import alaskaLoader from '@/assets/images/alaska-loader.gif';
import './AzureFunctionHealth.css';

/**
 * Render one compact dashboard summary metric.
 * @param {Object} props - Metric label, value, icon, and color.
 * @returns {JSX.Element} Summary metric.
 */
function SummaryMetric({ label, value, icon: Icon, color, explanation }) {
  return (
    <Tooltip
      title={explanation}
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={3000}
      placement="top"
    >
      <Paper
        className="afh-summary-metric"
        elevation={0}
        tabIndex={0}
        aria-label={`${label}: ${value}. ${explanation}`}
      >
        <Box className="afh-summary-icon" sx={{ color, backgroundColor: `${color}14` }}>
          <Icon size={20} />
        </Box>
        <Box>
          <Typography className="afh-summary-label">{label}</Typography>
          <Typography className="afh-summary-value">{value}</Typography>
        </Box>
      </Paper>
    </Tooltip>
  );
}

/**
 * Render the complete Azure Function Health portal module.
 * @returns {JSX.Element} Health dashboard page.
 */
export function AzureFunctionHealth() {
  const dashboard = useAzureFunctionHealth();
  const [bulkEnvironment, setBulkEnvironment] = React.useState('TEST');

  /**
   * Apply a compliance filter selected by the segmented control.
   * @param {React.MouseEvent} _event - Toggle interaction event.
   * @param {string|null} nextFilter - Selected filter value.
   * @returns {void}
   */
  const handleDashboardFilterChange = (_event, nextFilter) => {
    if (nextFilter) {
      dashboard.setSelectedDashboardFilter(nextFilter);
    }
  };

  const handleBulkEnvironmentChange = (event) => {
    const nextEnvironment = event.target.value;
    setBulkEnvironment(nextEnvironment);
    dashboard.setAllWidgetEnvironments(nextEnvironment);
  };

  return (
    <Box className="afh-page">
      <Box className="afh-header">
        <Box className="afh-title-block">
          <Typography variant="h4" component="h1">Azure Function Health</Typography>
          <Typography color="text.secondary">
            Monitor endpoint compliance and health across approved Azure Function environments.
          </Typography>
        </Box>
        <Box className="afh-control-row">
          <FormControl size="small" className="afh-catalog-selector">
            <InputLabel id="domain-selector-label">Domain</InputLabel>
            <Select
              labelId="domain-selector-label"
              value={dashboard.selectedDomain}
              label="Domain"
              onChange={(event) => dashboard.setSelectedDomain(event.target.value)}
              disabled={dashboard.isCatalogLoading}
            >
              {/* Created or modified by CoPilot AI Agent */}
              {dashboard.domains.map((domain) => (
                <MenuItem key={domain.id} value={domain.id}>{domain.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" className="afh-catalog-selector">
            <InputLabel id="pod-selector-label">Pod</InputLabel>
            <Select
              labelId="pod-selector-label"
              value={dashboard.selectedPod}
              label="Pod"
              onChange={(event) => dashboard.setSelectedPod(event.target.value)}
              disabled={dashboard.isCatalogLoading || dashboard.selectedDomainPods.length === 0}
            >
              {dashboard.selectedDomainPods.map((pod) => (
                <MenuItem key={pod.id} value={pod.id}>{pod.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" className="afh-bulk-environment-control">
            <InputLabel id="bulk-environment-label">All environments</InputLabel>
            <Select
              labelId="bulk-environment-label"
              value={bulkEnvironment}
              label="All environments"
              onChange={handleBulkEnvironmentChange}
              disabled={dashboard.isCatalogLoading || dashboard.widgets.length === 0}
            >
              {HEALTH_ENVIRONMENTS.map((environment) => (
                <MenuItem key={environment} value={environment}>{environment}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {/* Created or modified by CoPilot AI Agent */}
        <Box className="afh-action-row">
          <Tooltip title="By enabling notifications, you will receive an email any time a function call fails for the currently selected pod in the TEST environment. QA and PROD support will be enabled in the future." arrow>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={<Checkbox checked={dashboard.podNotify} disabled={dashboard.isCatalogLoading || !Number.isInteger(dashboard.selectedPod) || dashboard.isPodNotifySaving} onChange={(event) => dashboard.setPodNotify(event.target.checked)} />}
                label="Pod Notify"
              />
            </Box>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Play size={17} />}
            disabled={dashboard.isCatalogLoading || dashboard.selectedPodEndpointCount === 0}
            onClick={dashboard.runAllAvailableEndpoints}
          >
            Run all available endpoints
          </Button>
        </Box>
      </Box>

      {dashboard.catalogError && <Alert severity="error">{dashboard.catalogError}</Alert>}

      <Box className="afh-summary-grid" aria-label="Health summary">
        <SummaryMetric
          label="Fully Compliant"
          value={dashboard.fullyCompliantCount}
          icon={CheckCircle2}
          color="#01426A"
          explanation="Applications with both Head and Health endpoints present in the server catalog."
        />
        <SummaryMetric
          label="Partially Compliant"
          value={dashboard.partialCompliantCount}
          icon={CircleDashed}
          color="#8A5A00"
          explanation="Applications with exactly one of the Head or Health endpoints present in the server catalog."
        />
        <SummaryMetric
          label="Not Compliant"
          value={dashboard.notCompliantCount}
          icon={ShieldAlert}
          color="#C81E3A"
          explanation="Applications with neither a Head nor a Health endpoint present in the server catalog."
        />
        <SummaryMetric
          label="Healthy"
          value={dashboard.healthyCount}
          icon={CheckCircle2}
          color="#008653"
          explanation="Configured probes that most recently returned a successful response."
        />
        <SummaryMetric
          label="Unhealthy"
          value={dashboard.unhealthyCount}
          icon={ShieldAlert}
          color="#C81E3A"
          explanation="Configured probes that most recently returned an error or could not be reached."
        />
        <SummaryMetric
          label="Skipped"
          value={dashboard.skippedCount}
          icon={CircleOff}
          color="#5B6573"
          explanation="Probes intentionally not run because the scenario or environment is disabled."
        />
      </Box>

      <Box className="afh-filter-band">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={dashboard.selectedDashboardFilter}
          onChange={handleDashboardFilterChange}
          aria-label="Dashboard filters"
        >
          <ToggleButton value={DASHBOARD_FILTERS.FAVORITES}>Favorites</ToggleButton>
          <ToggleButton value={DASHBOARD_FILTERS.ALL}>All apps</ToggleButton>
          <ToggleButton value={DASHBOARD_FILTERS.FULLY_COMPLIANT}>Fully Compliant</ToggleButton>
          <ToggleButton value={DASHBOARD_FILTERS.PARTIAL_COMPLIANT}>Partially Compliant</ToggleButton>
          <ToggleButton value={DASHBOARD_FILTERS.NOT_COMPLIANT}>Not Compliant</ToggleButton>
          <ToggleButton value={DASHBOARD_FILTERS.HEALTHY}>Healthy</ToggleButton>
          <ToggleButton value={DASHBOARD_FILTERS.UNHEALTHY}>Unhealthy</ToggleButton>
          <ToggleButton value={DASHBOARD_FILTERS.SKIPPED}>Skipped</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {dashboard.isCatalogLoading ? (
        /* Created or modified by CoPilot AI Agent */
        <Box className="afh-loading-backdrop" role="status" aria-label="Loading health scenarios">
          <img src={alaskaLoader} alt="Loading health scenarios" className="afh-loading-throbber" />
        </Box>
      ) : dashboard.selectedPodEndpointCount === 0 ? (
        <Paper className="afh-empty-state" elevation={0}>
          <Typography variant="h6">No endpoints configured</Typography>
          <Typography color="text.secondary">
            {dashboard.selectedPodName
              ? `No Azure Function Health endpoints are currently mapped to ${dashboard.selectedPodName}.`
              : 'No pods are configured for this domain.'}
          </Typography>
        </Paper>
      ) : (
        <Box className="afh-scenario-grid" aria-label="Health probe scenarios">
          {dashboard.filteredWidgets.map((widget) => (
            <HealthScenarioCard
              key={widget.id}
              widget={widget}
              onEnvironmentChange={dashboard.setWidgetEnvironment}
              onRunEndpoint={dashboard.runEndpoint}
              onRunAll={dashboard.runAllForWidget}
              onClearFailure={dashboard.clearFailureNotice}
              onToggleFavorite={dashboard.toggleFavorite}
              onNotifyChange={dashboard.setWidgetNotify}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default AzureFunctionHealth;